// @flow

import React, { createContext, useContext } from 'react';
import type { Node } from 'react';
import { useImmerReducer } from 'use-immer';
import { closeFullscreenOverlay, initFullscreenOverlay, removeTabindex, restoreTabIndex } from 'utils';
import { BODY_ID, DESKTOP_NAV_ID } from 'Constants';

const ModalContextActions = {
  OPEN: 'OPEN',
  CLOSE: 'CLOSE',
  SET_VIDEO_SRC: 'SET_VIDEO_SRC',
};

type ActionsType =
  | {
      type: ModalContextActions.OPEN,
      payload: string,
    }
  | {
      type: ModalContextActions.CLOSE,
    }
  | {
      type: ModalContextActions.SET_VIDEO_SRC,
      payload: {
        src: string,
        poster: string,
      },
    };

type State = {
  isOpen: boolean,
  id: string,
  video: {
    src: string,
    poster: string,
  },
};

const initialState: State = {
  isOpen: false,
  id: '',
  video: {
    src: '',
    poster: '',
  },
};

const OVERLAY_CALLER_ID = 'modal';

const reducer = (draft: State, action: ActionsType): void => {
  const { type, payload } = action;
  switch (type) {
    case ModalContextActions.OPEN:
      initFullscreenOverlay(OVERLAY_CALLER_ID);
      removeTabindex(BODY_ID);
      removeTabindex(DESKTOP_NAV_ID);
      draft.isOpen = true;
      draft.id = payload;
      break;
    case ModalContextActions.CLOSE:
      closeFullscreenOverlay(OVERLAY_CALLER_ID);
      restoreTabIndex(BODY_ID);
      restoreTabIndex(DESKTOP_NAV_ID);
      draft.isOpen = false;
      break;
    case ModalContextActions.SET_VIDEO_SRC:
      draft.video = payload;
  }
};

const DispatchContext: any = createContext();
const StateContext: any = createContext();

const ModalContextProvider = ({ children }: { children: Node }) => {
  const [state, dispatch] = useImmerReducer(reducer, initialState);
  return (
    <StateContext.Provider value={state}>
      <DispatchContext.Provider value={dispatch}>{children}</DispatchContext.Provider>
    </StateContext.Provider>
  );
};

type Dispatch = (action: ActionsType) => void;

const useModalDispatch = (): Dispatch => useContext(DispatchContext);
const useModalState = (): State => useContext(StateContext);

export { ModalContextProvider, useModalDispatch, useModalState, ModalContextActions };
